import type { Aggregation, AggregationValue } from '@/types/job.types';

/**
 * Aggregation (facet) helpers shared by every jobs list: filter titles and equivalent-value
 * merging. The API modules run responses through mergeEquivalentAggregations, and the query
 * builders send every raw key a merged value stands for (facetKeys).
 */

/** Flutter OtherConstants.defaultAllMultiFilters — filter titles by aggregation name. */
const DEFAULT_ALL_MULTI_FILTERS: { key: string; title: string }[] = [
  { key: 'city', title: 'City' },
  { key: 'jobType', title: 'Job Type' },
  { key: 'experience', title: 'Experience' },
  { key: 'skills', title: 'Skills' },
  { key: 'employmentType', title: 'Employment Type' },
  { key: 'locations', title: 'Location' },
  { key: 'departments', title: 'Department' },
];

/**
 * Title of a filter, as Flutter Aggregations.fromJson sets it: looked up by name in the tenant
 * preference `allJobsFilters` (a `{ key, title }` list that replaces the defaults, see
 * GenericHelpers.setAllJobsFilters), and "Sort" for `sortBy`. The title is the translation key.
 */
export function getAggregationTitle(
  name: string | undefined,
  preferences: Record<string, unknown> | null | undefined,
): string | undefined {
  if (!name) return undefined;
  if (name === 'sortBy') return 'Sort';
  const configured = preferences?.allJobsFilters;
  const filters = Array.isArray(configured) && configured.length > 0
    ? (configured as { key?: unknown; title?: unknown }[])
    : DEFAULT_ALL_MULTI_FILTERS;
  const match = filters.find((f) => f?.key === name);
  return typeof match?.title === 'string' && match.title ? match.title : undefined;
}

/**
 * Comparison form of a facet key. The backend's `raw_lowercase` analyzer folds case but keeps
 * whitespace, so "engineering" and "engineering " come back as two buckets that both display
 * "Engineering".
 */
export function normalizeFacetKey(key: string | undefined): string {
  return (key ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

/** Raw backend keys a (possibly merged) value stands for — what the query string must carry. */
export function facetKeys(value: AggregationValue): string[] {
  if (value.mergedKeys?.length) return value.mergedKeys;
  return [value.key ?? value.displayKey ?? ''];
}

/**
 * Merge values whose keys differ only by case or surrounding whitespace into one value:
 * counts are summed, it is checked when any variant is, and `mergedKeys` lists every raw key.
 * Its `key` is the lexicographically smallest raw key, so it stays the same whatever order or
 * counts a later response has (selected filters are matched by `key`). The group takes the
 * position of its first variant.
 */
export function mergeEquivalentValues(values: AggregationValue[]): AggregationValue[] {
  const groups = new Map<string, AggregationValue[]>();
  for (const value of values) {
    const id = normalizeFacetKey(value.key ?? value.displayKey);
    const group = groups.get(id);
    if (group) group.push(value);
    else groups.set(id, [value]);
  }

  return Array.from(groups.values(), (group) => {
    if (group.length === 1) return group[0];
    const rawKeys = Array.from(new Set(group.flatMap(facetKeys))).sort();
    const canonical = group.find((v) => v.key === rawKeys[0]) ?? group[0];
    const counts = group.map((v) => v.count).filter((c): c is number => typeof c === 'number');
    return {
      ...canonical,
      key: rawKeys[0],
      displayKey: canonical.displayKey?.trim() ?? canonical.displayKey,
      count: counts.length ? counts.reduce((sum, c) => sum + c, 0) : canonical.count,
      isChecked: group.some((v) => v.isChecked) || undefined,
      mergedKeys: rawKeys,
    };
  });
}

/** mergeEquivalentValues for every facet of a response (the sort options are left alone). */
export function mergeEquivalentAggregations(aggregations: Aggregation[]): Aggregation[];
export function mergeEquivalentAggregations(aggregations: Aggregation[] | undefined): Aggregation[] | undefined;
export function mergeEquivalentAggregations(aggregations: Aggregation[] | undefined): Aggregation[] | undefined {
  if (!Array.isArray(aggregations)) return aggregations;
  return aggregations.map((agg) =>
    agg.name === 'sortBy' || !Array.isArray(agg.values)
      ? agg
      : { ...agg, values: mergeEquivalentValues(agg.values) },
  );
}
